import { isFetchResultSuccessful } from '@/entities/FetchResult'
import { LeaveOfAbsenceForm } from '@/entities/LeaveOfAbsences/LeaveOfAbsences'
import {
    cancelLeaveOfAbsenceApiCall,
    cancelLeaveOfAbsenceChangeRequestApiCall,
    getLeaveOfAbsencesDetailApiCall,
    requestLeaveOfAbsenceChangeApiCall,
} from '@/entities/LeaveOfAbsences/LeaveOfAbsencesService'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '@/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '@/entities/NotificationCenter/NotificationCenterService'
import { trans } from '@/helpers/trans'
import { TypedDispatch } from '@/redux/store'
import { setToast } from '@/redux/toasts/toastsReducer'
import { ToastType } from '@/types'

import {
    setError,
    setIsLoading,
    setIsSuccessful,
    setLeaveOfAbsenceRequestDetail,
} from './leaveOfAbsenceRequestDetailReducer'

export const fetchLeaveOfAbsencesDetail = (id: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    try {
        const response = await getLeaveOfAbsencesDetailApiCall(id)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setIsLoading(false))

            return
        }

        dispatch(setLeaveOfAbsenceRequestDetail(response.data))
    } catch (error) {
        console.error('[fetchLeaveOfAbsencesDetail]', error)

        dispatch(setError('Error fetching data'))
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const cancelLeaveOfAbsence = (id: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsSuccessful(false))

    try {
        const response = await cancelLeaveOfAbsenceApiCall(id)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setIsLoading(false))

            return
        }

        dispatch(setToast({
            text: trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceRequestCancel'),
            type: ToastType.pass,
        }))
        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[cancelLeaveOfAbsence]', error)
        dispatch(setError('Error cancelling leave of absence'))
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const requestLeaveOfAbsenceChange = (
    id: string,
    leaveOfAbsence: LeaveOfAbsenceForm,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsSuccessful(false))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.LEAVE_OF_ABSENCE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SOURCE_EMPLOYEE_APP],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await requestLeaveOfAbsenceChangeApiCall(id, leaveOfAbsence, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setIsLoading(false))

            return
        }

        dispatch(setLeaveOfAbsenceRequestDetail(response.data))
        dispatch(setToast({
            text: trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceChangeRequestSubmitted'),
            type: ToastType.pass,
        }))
        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[requestLeaveOfAbsenceChange]', error)
        dispatch(setError('Error requesting leave of absence change'))
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const cancelLeaveOfAbsenceChangeRequest = (
    id: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsSuccessful(false))

    try {
        const response = await cancelLeaveOfAbsenceChangeRequestApiCall(id)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setIsLoading(false))

            return
        }

        dispatch(setToast({
            text: trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceChangeRequestCancelled'),
            type: ToastType.pass,
        }))
        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[cancelLeaveOfAbsenceChangeRequest]', error)
        dispatch(setError('Error cancelling leave of absence change request'))
    } finally {
        dispatch(setIsLoading(false))
    }
}
